var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { Protocol } from './types';
import { objectToQueryString, popupCenter, isWechatBrowser } from '../utils';
import { generateUidKey } from '../utils';
import { BaseAuthenticationClient } from './BaseAuthenticationClient';
/**
 * @class EnterpriseAuthenticationClient
 * @description This client encapsulates the function of Enterprise Authentication, and can quickly use Enterprise Authentication to obtain user information through a simple API.
 *
 * Initiate a login authorization request for the Enterprise Authentication：
 *
 * \`\`\`javascript
 * import { AuthenticationClient } from "approw-js-sdk"
 * const authenticationClient = new AuthenticationClient({
 *    appId: "YOUR_APP_ID",
 * })
 * await authenticationClient.enterprise.authorize("oidc", "identifierxxx" {
 *    onSuccess: (user) => { console.log(user) },
 *    onError: (code, message) => {  }
 * })
 * \`\`\`
 *
 *
 * @name EnterpriseAuthenticationClient
 */
var EnterpriseAuthenticationClient = /** @class */ (function () {
    function EnterpriseAuthenticationClient(options, tokenProvider, httpClient) {
        this.options = options;
        this.baseClient = new BaseAuthenticationClient(options);
        this.tokenProvider = tokenProvider;
        this.httpClient = httpClient;
    }
    //  TODO, follow the user-portal logic, and it will be optimized later
    EnterpriseAuthenticationClient.prototype.initProviderContext = function (appId) {
        return __awaiter(this, void 0, void 0, function () {
            var appConfig;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.httpClient.request({
                            method: 'GET',
                            url: this.baseClient.appHost + "/api/v2/applications/" + appId + "/public-config"
                        })];
                    case 1:
                        appConfig = _a.sent();
                        return [2 /*return*/, appConfig];
                }
            });
        });
    };
    EnterpriseAuthenticationClient.prototype.getProviderConfigAndError = function (protocol, appConfig, identifier) {
        var _a, _b;
        var providerConfig = (_b = (_a = appConfig.identityProviders) === null || _a === void 0 ? void 0 : _a.find(function (item) { return item.protocol === protocol && item.identifier === identifier; })) === null || _b === void 0 ? void 0 : _b.config;
        if (!providerConfig) {
            throw new Error('Identity provider configuration does not exist');
        }
        return providerConfig;
    };
    EnterpriseAuthenticationClient.prototype.getLoginUrl = function (protocol, appConfig, identifier, state) {
        var config = this.getProviderConfigAndError(protocol, appConfig, identifier);
        switch (protocol) {
            case Protocol.OIDC:
                config = config;
                var query = objectToQueryString({
                    state: state,
                    client_id: config.clientId,
                    redirect_uri: config.redirectUri,
                    scope: config.scopes,
                    response_type: config.responseType,
                    nonce: generateUidKey(32)
                });
                return config.authorizationEdpoint + "?" + query;
            case Protocol.SAML:
                config = config;
                return config.samlRequest;
            case Protocol.CAS:
                config = config;
                return config.casConnectionLoginUrl;
            case Protocol.OAUTH:
                config = config;
                return config.authUrl;
            case Protocol.AZURE_AD:
                config = config;
                return config.authorizationUrl;
            default:
                return '';
        }
    };
    /**
     * @name authorize
     * @name_zh Send authorization login request
     * @description Send an authorized login request. This method will directly open a new window and jump to the login authorization page of the enterprise identity provider  (such as OIDC, SAML, etc.).
     * After the user completes the authorization, this window will be automatically closed and the onSuccess callback function will be triggered. With this function, you can get user information.
     *
     * @param {Protocol} protocol: enterprise identity provider protocol
     * @param {string} provider enterprise identity provider identifier
     * @param {object} [options]
     * @param {boolean} [options.popup=true] Whether to open the social login window through a pop-up window, if set to false, a new browser tab will be opened in the form of window.open.
     * @param {Function} [options.onSuccess] The user agrees to the authorization event callback function, the first parameter is user information.
     * @param {Function} [options.onError] The callback function of the enterprise identity provider failure event, the first parameter code is the error code, and the second parameter message is the error prompt. Please see the detailed error code list:[Approw error code list](https://docs.approw.com/reference/error-code.html)
     * @param {object} [options.position] Only valid when options.popup is ture. The default position of the pop-up window is { w: 585, h: 649 } 。
     *
     * @example
     *
     * // Log in with the OIDC identity provider whose identity provider identifier is'oidc1'
     *
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     *
     * await authenticationClient.enterprise.authorize("oidc", "oidc1" {
     *    onSuccess: (user) => { console.log(user) },
     *    onError: (code, message) => {  },
     *    // Customize the position of the pop-up window
     *    position: {
     *      w: 100,
     *      h: 100
     *    }
     * })
     *
     * @example
     *
     * // Use the new browser tab to open the social login page
     *
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     *
     * await authenticationClient.enterprise.authorize("oidc", "oidc1", {
     *    popup: false,
     *    onSuccess: (user) => { console.log(user) },
     *    onError: (code, message) => {  },
     * })
     *
     * @memberof EnterpriseAuthenticationClient
     */
    EnterpriseAuthenticationClient.prototype.authorize = function (protocol, identifier, options) {
        return __awaiter(this, void 0, void 0, function () {
            var position, _a, popup, onSuccess, onError, appId, appConfig, state, url, onMessage;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        options = options || {};
                        position = options.position, _a = options.popup, popup = _a === void 0 ? true : _a, onSuccess = options.onSuccess, onError = options.onError;
                        appId = this.options.appId;
                        return [4 /*yield*/, this.initProviderContext(appId)];
                    case 1:
                        appConfig = _b.sent();
                        state = generateUidKey(32);
                        if (!(protocol === Protocol.OIDC)) return [3 /*break*/, 3];
                        return [4 /*yield*/, this.httpClient.request({
                                method: 'POST',
                                url: this.baseClient.appHost + "/api/v2/connections/oidc/start-interaction",
                                data: {
                                    state: state,
                                    protocol: protocol,
                                    userPoolId: appConfig.userPoolId,
                                    appId: appId,
                                    referer: window.location.href,
                                    connection: { providerIentifier: identifier }
                                }
                            })];
                    case 2:
                        _b.sent();
                        _b.label = 3;
                    case 3:
                        url = this.getLoginUrl(protocol, appConfig, identifier, state);
                        onMessage = function (e) {
                            var _a = e.data, code = _a.code, message = _a.message, userInfo = _a.data, event = _a.event;
                            event = event || {};
                            var source = event.source, eventType = event.eventType;
                            if (source !== 'approw' || eventType !== 'enterpriseLogin') {
                                return;
                            }
                            try {
                                var parsedMsg = JSON.parse(message);
                                message = parsedMsg.message;
                                code = parsedMsg.code;
                            }
                            catch (e) {
                                // do nothing...
                            }
                            if (code === 200) {
                                // save user's token
                                _this.tokenProvider.setUser(userInfo);
                                onSuccess && onSuccess(userInfo);
                            }
                            else {
                                onError && onError(code, message);
                            }
                            window.removeEventListener('message', onMessage);
                        };
                        window.addEventListener('message', onMessage);
                        if (isWechatBrowser()) {
                            // Open in WeChat
                            window.location.href = url;
                        }
                        else if (popup) {
                            popupCenter(url, position);
                        }
                        else {
                            window.open(url);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    return EnterpriseAuthenticationClient;
}());
export { EnterpriseAuthenticationClient };
//# sourceMappingURL=data:application/json;base64,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